import { useMutation } from '@tanstack/react-query'
import {
  userJourneyApi,
  userRegisterApi,
  userSearchApi,
  userAnalyticsEventApi,
  userSubscriptionAnalytics,
  contentTrackAnalytics,
  contentPlayerAnalytics,
  playedMatricsAnalytics
} from '../api/analyticsApi'
import { useSelector } from 'react-redux'
import { CMS_EVENTS } from 'shared-ui/utils/gaConstants'
import { v4 as uuidv4 } from 'uuid'
import { useResponsive } from 'shared-ui/hooks'
import { media } from 'shared-ui/utils/media.js'
import { matchPath, useLocation } from 'react-router-dom'
import { HIDE_TRACK_API_ROUTES,APP_VERSION } from 'shared-ui/utils/constants'
import { getEventTimestamp } from 'shared-ui/utils/helper.jsx'
import countriesJSONData from 'shared-ui/utils/Countries.json'
const ANALYTICS_ENABLED = process.env.REACT_APP_ANALYTICS_ENABLED === 'true'

const PLAYER_ANALYTICS_ENABLED =
  process.env.REACT_APP_PLAYER_ANALYTICS_ENABLED === 'true'

export const getIsoEventTimestamp = () => getEventTimestamp().replace(' ', 'T')

export const getCurrentCountry = () => {
  const countryCode = localStorage.getItem('currentCountry')

  return countriesJSONData.find(
    (item) => item?.country_code === countryCode
  )?.country
}

const getDeviceInfo = () => {
  const ua = navigator.userAgent.toLowerCase()

  let OS = 'Unknown'
  let deviceCategory = 'Desktop'
  let browserName = 'Unknown'

  // OS + Version
  if (/android/.test(ua)) {
    const version = ua.match(/android\s([0-9.]+)/i)?.[1]
    OS = version ? `Android ${version}` : 'Android'
  } else if (/iphone|ipad|ipod/.test(ua)) {
    const version = ua.match(/os\s([0-9_]+)/i)?.[1]?.replace(/_/g, '.')
    OS = version ? `iOS ${version}` : 'iOS'
  } else if (/windows/.test(ua)) {
    const version = ua.match(/windows nt\s([0-9.]+)/i)?.[1]
    OS = version ? `Windows ${version}` : 'Windows'
  } else if (/mac os/.test(ua)) {
    const version = ua.match(/mac os x\s([0-9_]+)/i)?.[1]?.replace(/_/g, '.')
    OS = version ? `macOS ${version}` : 'macOS'
  } else if (/linux/.test(ua)) {
    OS = 'Linux'
  }

  // Device Category
  if (/ipad|tablet/.test(ua)) {
    deviceCategory = 'Tablet'
  } else if (/iphone|ipod/.test(ua)) {
    deviceCategory = 'iOS mobile'
  } else if (/android/.test(ua)) {
    deviceCategory = 'Android mobile'
  } else {
    deviceCategory = 'Desktop'
  }

  // Browser
  if (/edg\//.test(ua)) {
    browserName = 'Edge'
  } else if (/chrome\//.test(ua) && !/edg\//.test(ua)) {
    browserName = 'Chrome'
  } else if (/firefox\//.test(ua)) {
    browserName = 'Firefox'
  } else if (/safari\//.test(ua) && !/chrome\//.test(ua)) {
    browserName = 'Safari'
  } else if (/opr\//.test(ua) || /opera/.test(ua)) {
    browserName = 'Opera'
  }

  return {
    OS,
    deviceCategory,
    browserName
  }
}

// register
export const useUserRegister = (options = {}) => {
  return useMutation({
    mutationFn: (payload) => {
      if (!ANALYTICS_ENABLED) return
      return userRegisterApi(payload)
    },
    ...options
  })
}

//P0 events
export const useUserAnalyticsEvent = (options = {}) => {
  const user = useSelector((state) => state.auth.user)
  const isMobile = useResponsive(media.mobile)

  const localSubscription = JSON.parse(
    localStorage.getItem('subscriptionStatus') || '{}'
  )

  const isSubscribe =
    localSubscription?.data?.status === 'active' ? 'true' : 'false'
  const updatePlatform = isMobile
    ? CMS_EVENTS.PLATFORM_MWEB
    : CMS_EVENTS.PLATFORM

  const mutation = useMutation({
    mutationFn: userAnalyticsEventApi,
    ...options
  })

  const logCmsEvent = (eventName, parameters = {}) => {
    if (!ANALYTICS_ENABLED) return
    const country = getCurrentCountry()
    const localUser = JSON.parse(localStorage.getItem('user')) || {}
    const currentUser = user || localUser
    let uuid = currentUser?.user_id
    let isGuest = 'false'

    if (!uuid) {
      uuid = sessionStorage.getItem('guestUuid')
      isGuest = 'true'
    }
    const deviceId = localStorage.getItem('deviceId')

    const eventParameters = Object.fromEntries(
      Object.entries({
        user_id: uuid,
        creator_id: uuid,
        version: APP_VERSION || '1.0.0',
        device_id: deviceId,
        country: country,
        time_stamp: getEventTimestamp(),
        is_guest: isGuest,
        is_subscriber: isSubscribe,
        platform: updatePlatform,
        ...parameters
      }).filter(([, value]) => value != null)
    )
    mutation.mutate({
      event_name: eventName,
      parameters: eventParameters
    })
  }

  return {
    logCmsEvent,
    ...mutation
  }
}

// subscription replica events
export const useUserSubscriptionAnalytics = (options = {}) => {
  const user = useSelector((state) => state.auth.user)
  const isMobile = useResponsive(media.mobile)
  const mutation = useMutation({
    mutationFn: userSubscriptionAnalytics,
    ...options
  })

  const logSubscriptionEvent = (eventName, parameters = {}) => {
    if (!ANALYTICS_ENABLED) return
    const localUser = JSON.parse(localStorage.getItem('user')) || {}
    const country = getCurrentCountry()
    const currentUser = user || localUser
    const updatePlatform = isMobile
      ? CMS_EVENTS.PLATFORM_MWEB
      : CMS_EVENTS.PLATFORM

    mutation.mutate({
      status: eventName,
      country: country,
      platform: updatePlatform,
      partnerName: CMS_EVENTS.PARTNER_NAME,
      uuid: currentUser?.user_id,
      ...Object.fromEntries(
        Object.entries(parameters).filter(([, value]) => value != null)
      )
    })
  }
  return {
    logSubscriptionEvent,
    ...mutation
  }
}

// user journey replica events
export const useUserJourney = (options = {}) => {
  const user = useSelector((state) => state.auth.user)
  const mutation = useMutation({
    mutationFn: userJourneyApi,
    ...options
  })

  const sendUserJourney = (eventName, parameters = {}) => {
    if (!ANALYTICS_ENABLED) return
    const localUser = JSON.parse(localStorage.getItem('user')) || {}
    const localSubscription = JSON.parse(
      localStorage.getItem('subscriptionStatus') || '{}'
    )
    const currentUser = user || localUser
    const isSubscribe = localSubscription?.data?.status === 'active'
    const country = getCurrentCountry()
    const { OS, deviceCategory, browserName } = getDeviceInfo()

    let sessionId = sessionStorage.getItem('sessionId')

    if (!sessionId) {
      sessionId = uuidv4()
      sessionStorage.setItem('sessionId', sessionId)
    }

    let updatedUuid = currentUser?.user_id

    if (updatedUuid) {
      // User is logged in, remove guest UUID
      sessionStorage.removeItem('guestUuid')
    } else {
      // Guest user
      updatedUuid = sessionStorage.getItem('guestUuid')

      if (!updatedUuid) {
        updatedUuid = `guest_${uuidv4()}`
        sessionStorage.setItem('guestUuid', updatedUuid)
      }
    }
    mutation.mutate({
      eventType: eventName,
      partnerName: CMS_EVENTS.PARTNER_NAME,
      sessionId,
      uuid: updatedUuid,
      country: country,
      subscription_status: isSubscribe,
      app_version: APP_VERSION,
      device_category:deviceCategory,
      OS,
      browser_name:browserName,
      app_name: CMS_EVENTS.PARTNER_NAME,
      timeStamp: getIsoEventTimestamp(),
      eventDate: getIsoEventTimestamp(),
      ...Object.fromEntries(
        Object.entries(parameters).filter(([, value]) => value != null)
      )
    })
  }
  return {
    sendUserJourney,
    ...mutation
  }
}

// search events
export const useUserSearch = (options = {}) => {
  const isMobile = useResponsive(media.mobile)
  const user = useSelector((state) => state.auth.user)

  const mutation = useMutation({
    mutationFn: (payload) => userSearchApi(payload),
    ...options
  })
  const updatePlatform = isMobile
    ? CMS_EVENTS.PLATFORM_MWEB
    : CMS_EVENTS.PLATFORM

  const sendUserSearch = (parameters = {}) => {
    if (!ANALYTICS_ENABLED) return
    const localUser = JSON.parse(localStorage.getItem('user')) || {}
    const currentUser = user || localUser
    let uuid = currentUser?.user_id

    if (!uuid) {
      uuid = sessionStorage.getItem('guestUuid')
    }

    const sessionId = sessionStorage.getItem('sessionId')
    mutation.mutate({
      eventName: CMS_EVENTS.SEARCH_EVENT,
      sessionId,
      eventValue: {
        uuid,
        platform: updatePlatform,
        event_date: getIsoEventTimestamp(),
        ...Object.fromEntries(
          Object.entries(parameters).filter(([, value]) => value != null)
        )
      }
    })
  }

  return {
    sendUserSearch,
    ...mutation
  }
}

// track events
export const useTrackAnalytics = (options = {}) => {
  const location = useLocation()
  const user = useSelector((state) => state.auth.user)
  const restrictedCall = HIDE_TRACK_API_ROUTES.some((route) =>
    matchPath(route, location.pathname)
  )

  const mutation = useMutation({
    mutationFn: (payload) => contentTrackAnalytics(payload),
    ...options
  })

  const sendEvent = (eventName, parameters = {}) => {
    if (!PLAYER_ANALYTICS_ENABLED || restrictedCall) return
    const localUser = JSON.parse(localStorage.getItem('user')) || {}
    const currentUser = user || localUser
    let uuid = currentUser?.user_id

    if (!uuid) {
      uuid = sessionStorage.getItem('guestUuid')
    }
    const deviceId = localStorage.getItem('deviceId')
    const { OS, deviceCategory, browserName } = getDeviceInfo()
    const country = getCurrentCountry()
    const sessionId = sessionStorage.getItem('sessionId')
    mutation.mutate({
      eventName: eventName,
      eventTimestamp: getEventTimestamp(),
      superProperties: {
        uuid,
        deviceId,
        deviceCategory,
        OS,
        playerId: CMS_EVENTS.PLAYER_ID,
        playerName: CMS_EVENTS.PLAYER_ID,
        platform: CMS_EVENTS.PLATFORM,
        appName: CMS_EVENTS.PARTNER_NAME,
        partner: CMS_EVENTS.PARTNER_NAME,
        country: country,
        networkType: 'WIFI',
        appVersion: APP_VERSION || '1.0.0'
      },
      eventProperties: {
        sessionId,
        isFree: 1,
        eventDate: getEventTimestamp(),
        browserName,
        ...Object.fromEntries(
          Object.entries(parameters).filter(([, value]) => value != null)
        )
      }
    })
  }

  return {
    sendEvent,
    ...mutation
  }
}

// played events
export const usePlayedAnalytics = (options = {}) => {
  const user = useSelector((state) => state.auth.user)

  const mutation = useMutation({
    mutationFn: (payload) => contentPlayerAnalytics(payload),
    ...options
  })

  const sendPlayedEvent = (eventName, parameters = {}) => {
    if (!PLAYER_ANALYTICS_ENABLED) return
    const localUser = JSON.parse(localStorage.getItem('user')) || {}
    const country = getCurrentCountry()
    const currentUser = user || localUser
    let uuid = currentUser?.user_id

    if (!uuid) {
      uuid = sessionStorage.getItem('guestUuid')
    }
    const deviceId = localStorage.getItem('deviceId')
    const { OS, deviceCategory, browserName } = getDeviceInfo()
    const sessionId = sessionStorage.getItem('sessionId')
    mutation.mutate({
      eventName: eventName,
      sessionId,
      eventValue: {
        uuid,
        deviceId,
        deviceCategory,
        OS,
        country: country,
        playerId: CMS_EVENTS.PLAYER_ID,
        playerName: CMS_EVENTS.PLAYER_ID,
        platform: CMS_EVENTS.PLATFORM,
        appName: CMS_EVENTS.PARTNER_NAME,
        partner: CMS_EVENTS.PARTNER_NAME,
        appVersion: APP_VERSION || '1.0.0',
        isFree: 1,
        browserName,
        ...Object.fromEntries(
          Object.entries(parameters).filter(([, value]) => value != null)
        )
      }
    })
  }

  return {
    sendPlayedEvent,
    ...mutation
  }
}

export const usePlayerMatrics = (options = {}) => {
  const user = useSelector((state) => state.auth.user)

  const mutation = useMutation({
    mutationFn: (payload) => playedMatricsAnalytics(payload),
    ...options
  })

  const sendPlayedMatricsEvent = (parameters = {}) => {
    if (!PLAYER_ANALYTICS_ENABLED) return
    const localUser = JSON.parse(localStorage.getItem('user')) || {}
    const currentUser = user || localUser
    let uuid = currentUser?.user_id

    if (!uuid) {
      uuid = sessionStorage.getItem('guestUuid')
    }
    const deviceId = localStorage.getItem('deviceId')
    const sessionId = sessionStorage.getItem('sessionId')
    mutation.mutate({
      session_id: sessionId,
      event_name: CMS_EVENTS.VIDEO_CONTENT_END,
      uuid,
      device_id: deviceId,
      player_id: CMS_EVENTS.PLAYER_ID,
      player_name: CMS_EVENTS.PLAYER_ID,
      partner_name: CMS_EVENTS.PARTNER_NAME,
      event_date: getEventTimestamp(),
      time_stamp: getEventTimestamp(),
      exit_before_start: 0,

      ...Object.fromEntries(
        Object.entries(parameters).filter(([, value]) => value != null)
      )
    })
  }

  return {
    sendPlayedMatricsEvent,
    ...mutation
  }
}